import React, { useCallback, useMemo } from 'react';
/**
 * Hook for translating multi-language text objects
 *
 * @example
 * ```tsx
 * const { t, locale } = useTranslation({ defaultLanguage: 'en' });
 *
 * const multiLangText = {
 *   en: 'Hello',
 *   fr: 'Bonjour',
 *   es: 'Hola'
 * };
 *
 * // Translates to current locale
 * const translated = t(multiLangText);
 *
 * // With fallback
 * const withFallback = t(multiLangText, 'Default text');
 *
 * // With params interpolation
 * const withParams = t('User has {count} points', '', { count: 10 });
 * // Result: 'User has 10 points'
 *
 * // Escaped brackets are treated as literal text
 * const escaped = t('Use \\{count\\} as placeholder', '', { count: 10 });
 * // Result: 'Use {count} as placeholder'
 *
 * // With function params for tag wrapping
 * const withTags = t('This is <i>formatted</i>', '', {
 *   i: (value) => <b>{value}</b>
 * });
 * // Result: ReactNode with <b>formatted</b>
 *
 * // Escaped tags are treated as literal text
 * const escapedTags = t('This is \\<i>not formatted\\</i>', '', {
 *   i: (value) => <b>{value}</b>
 * });
 * // Result: 'This is <i>not formatted</i>'
 *
 * // Mix of placeholders and tags
 * const mixed = t('User {name} has <b>{count}</b> points', '', {
 *   name: 'John',
 *   count: 10,
 *   b: (value) => <strong>{value}</strong>
 * });
 * // Result: ReactNode with 'User John has <strong>10</strong> points'
 *
 * // Override locale
 * const inFrench = t(multiLangText, '', undefined, 'fr');
 * ```
 */
export const useTranslation = (options = {}) => {
    const { defaultLanguage = 'en', currentLanguage } = options;
    // Get the current language from query params, browser, or fallback to default
    const locale = useMemo(() => {
        // Normalizes language codes like 'fr-FR' or 'pt_BR' to just 'fr' / 'pt'
        const normalizeLanguageCode = (code) => {
            if (!code)
                return undefined;
            return code.split(/[-_]/)[0].toLowerCase();
        };
        if (currentLanguage)
            return currentLanguage;
        // Check for language query parameter
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const langFromQuery = urlParams.get('locale');
            const normalizedFromQuery = normalizeLanguageCode(langFromQuery);
            if (normalizedFromQuery)
                return normalizedFromQuery;
        }
        // Try to get browser language
        if (typeof navigator !== 'undefined') {
            const browserLang = normalizeLanguageCode(navigator.language); // e.g., 'en-US' -> 'en'
            if (browserLang)
                return browserLang;
        }
        return defaultLanguage;
    }, [defaultLanguage, currentLanguage]);
    const t = useCallback((text, fallback, params, languageCode) => {
        // Helper function to interpolate params into the translated string
        const interpolate = (str, params) => {
            if (!params) {
                // Even without params, we need to unescape any escaped characters
                return str.replace(/\\([{}<>])/g, '$1');
            }
            // Replace escaped characters with sentinels so they are not matched
            // by tag/placeholder regexes (avoids lookbehind which breaks older Safari)
            const ESC_LT = '\x00ELT\x00';
            const ESC_GT = '\x00EGT\x00';
            const ESC_LB = '\x00ELB\x00';
            const ESC_RB = '\x00ERB\x00';
            const restoreSentinels = (s) => s.replace(/\x00ELT\x00/g, '<')
                .replace(/\x00EGT\x00/g, '>')
                .replace(/\x00ELB\x00/g, '{')
                .replace(/\x00ERB\x00/g, '}');
            str = str
                .replace(/\\</g, ESC_LT)
                .replace(/\\>/g, ESC_GT)
                .replace(/\\{/g, ESC_LB)
                .replace(/\\}/g, ESC_RB);
            // First, process tag-based interpolation for functions
            // Match tags like <i>content</i> (escaped tags are already replaced with sentinels)
            const tagRegex = /<(\w+)>(.*?)<\/\1>/g;
            const hasTags = tagRegex.test(str);
            if (hasTags) {
                // Reset regex lastIndex for reuse
                tagRegex.lastIndex = 0;
                const parts = [];
                let lastIndex = 0;
                let match;
                let hasReactNodes = false;
                while ((match = tagRegex.exec(str)) !== null) {
                    const [fullMatch, tagName, content] = match;
                    const param = params[tagName];
                    // Add text before the match
                    if (match.index > lastIndex) {
                        parts.push(str.substring(lastIndex, match.index));
                    }
                    // If param is a function, call it with the content
                    if (typeof param === 'function') {
                        parts.push(param(content));
                        hasReactNodes = true;
                    }
                    else {
                        // If not a function, keep the original tag
                        parts.push(fullMatch);
                    }
                    lastIndex = match.index + fullMatch.length;
                }
                // Add remaining text after last match
                if (lastIndex < str.length) {
                    parts.push(str.substring(lastIndex));
                }
                // If we have React nodes, process placeholder interpolation for each string part
                if (hasReactNodes) {
                    const processedParts = parts.map((part, index) => {
                        if (typeof part === 'string') {
                            // Process {key} placeholders in string parts
                            let processed = Object.entries(params).reduce((acc, [key, value]) => {
                                if (typeof value !== 'function') {
                                    const regex = new RegExp(`\\{${key}\\}`, 'g');
                                    return acc.replace(regex, String(value));
                                }
                                return acc;
                            }, part);
                            // Restore sentinels to literal characters
                            processed = restoreSentinels(processed);
                            return processed;
                        }
                        return part;
                    });
                    // If only one part and it's a string, return as string
                    if (processedParts.length === 1 && typeof processedParts[0] === 'string') {
                        return processedParts[0];
                    }
                    // Return array of React nodes
                    return processedParts.map((part, index) => typeof part === 'string' ? part : React.createElement('span', { key: index }, part));
                }
                // If no React nodes, continue with normal string processing
                str = parts.join('');
            }
            // Process {key} placeholders for string/number values
            let result = Object.entries(params).reduce((acc, [key, value]) => {
                if (typeof value !== 'function') {
                    const regex = new RegExp(`\\{${key}\\}`, 'g');
                    return acc.replace(regex, String(value));
                }
                return acc;
            }, str);
            // Restore sentinels to literal characters (\{ -> {, \} -> }, \< -> <, \> -> >)
            result = restoreSentinels(result);
            return result;
        };
        if (!text)
            return interpolate(fallback || '', params);
        if (typeof text === 'string')
            return interpolate(text, params);
        const targetLocale = languageCode || currentLanguage || locale;
        let translatedText = '';
        if (text[targetLocale]) {
            translatedText = text[targetLocale];
        }
        else if (fallback) {
            translatedText = fallback;
        }
        else if (text[defaultLanguage]) {
            translatedText = text[defaultLanguage];
        }
        else {
            const firstAvailable = Object.values(text).find((value) => value !== undefined && value !== '');
            translatedText = firstAvailable || '';
        }
        return interpolate(translatedText, params);
    }, [locale, defaultLanguage]);
    return { t, locale };
};
